/**
 * Riftbound: the Legend and its Chosen Champion share a champion identity
 * (e.g. "Darius"), not the legend title ("Darius, Hand of Noxus").
 */

import { isNonChampionSubtypeTag, isRegionTag } from '../../shared/cardTags.js';
import {
  championIdentityFromName,
  foldLegendIdentity,
  legendIdentityFromRef,
  type LegendIdentityRef,
} from '../../shared/legendIdentity.js';
import { cardTypeLineLabel } from './cardTypes';

export { championIdentityFromName, legendIdentityFromRef, type LegendIdentityRef };

/** Minimal card fields for champion-unit detection and legend matching (deck builder, modals, DnD). */
export type ChampionCardRef = {
  name?: string;
  cardType?: string;
  cardTypes?: string[];
  supertype?: string;
  champion?: string;
  tags?: string[];
};

function isChampionIdentityCandidate(tag: string): boolean {
  return Boolean(tag) && !isRegionTag(tag) && !isNonChampionSubtypeTag(tag);
}

export function legendDisplayName(identity: string): string {
  return identity
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Catalog tag shared by a legend, its champion units, and its signature spell or gear.
 * "Renekton" on the legend is the same tag on Dominus and on Renekton's champion units.
 * Prefers the printed tag so casing and apostrophes match the card (`Kai'Sa`, not `Kai'sa`).
 */
export function legendNameTag(
  card?: (LegendIdentityRef & { tags?: string[] }) | null,
): string {
  if (!card) return '';
  const identity = legendIdentityFromRef(card);
  if (!identity) return '';
  const match = (card.tags ?? [])
    .map((tag) => tag.trim())
    .find((tag) => tag && foldLegendIdentity(tag) === identity);
  if (match) return match;
  const champion = card.champion?.trim();
  if (champion && foldLegendIdentity(champion) === identity && champion !== identity) {
    return champion;
  }
  return legendDisplayName(identity);
}

/** True when `card` carries the legend's name tag (region and gear tags are ignored by mismatch). */
export function cardHasLegendNameTag(
  card: { tags?: string[] },
  legendTag: string,
): boolean {
  const wanted = foldLegendIdentity(legendTag);
  if (!wanted) return false;
  return (card.tags ?? []).some((tag) => foldLegendIdentity(tag) === wanted);
}

/**
 * Short champion label for match records and posters: "Ahri - Nine-Tailed Fox"
 * and "Ambessa, Matriarch of War" both become "Ahri" / "Ambessa". Returns null
 * when the string looks like a list name ("Jinx Aggro") rather than a print.
 */
export function normalizedLegendLabel(name?: string | null): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  const identity = legendIdentityFromRef({ name: trimmed });
  if (!identity) return null;
  const isPrintTitle =
    foldLegendIdentity(trimmed) === identity || /[,]|(\s[-–—]\s)/.test(trimmed);
  if (!isPrintTitle) return null;
  return legendDisplayName(identity);
}

function tagChampionIdentity(tags: string[] | undefined, name?: string): string {
  const candidates = (tags ?? [])
    .map((t) => t.trim())
    .filter((t) => isChampionIdentityCandidate(t));
  if (candidates.length === 0) return '';

  const nameLower = name?.trim().toLowerCase() ?? '';
  if (nameLower) {
    const nameMatch = candidates.find((tag) => {
      const tagLower = tag.toLowerCase();
      return nameLower === tagLower || nameLower.startsWith(`${tagLower} -`);
    });
    if (nameMatch) return nameMatch.toLowerCase();
  }

  return candidates.length === 1 ? candidates[0].toLowerCase() : '';
}

/** Stricter than {@link tagChampionIdentity}: avoids treating creature tags (e.g. Bird) as champions. */
function championUnitIdentityFromTags(tags: string[] | undefined, name: string): string {
  const trimmedName = name.trim();
  if (!trimmedName) return '';

  const candidates = (tags ?? [])
    .map((t) => t.trim())
    .filter((t) => isChampionIdentityCandidate(t));
  if (candidates.length === 0) return '';

  const nameLower = trimmedName.toLowerCase();
  const fromName = championIdentityFromName(trimmedName);

  const nameMatch = candidates.find((tag) => {
    const tagLower = tag.toLowerCase();
    return (
      nameLower === tagLower ||
      nameLower.startsWith(`${tagLower} -`) ||
      nameLower.startsWith(`${tagLower},`)
    );
  });
  if (nameMatch) return nameMatch.toLowerCase();

  if (candidates.length === 1 && fromName) {
    const tag = candidates[0].toLowerCase();
    if (fromName === tag) return tag;
  }

  return '';
}

function identityFromRef(ref: { champion?: string; name: string; tags?: string[] }): string {
  const fromTags = tagChampionIdentity(ref.tags, ref.name);
  if (fromTags) return fromTags;
  const raw = ref.champion?.trim().toLowerCase();
  const fromName = championIdentityFromName(ref.name);
  if (!raw) return fromName;
  // Older cached rows sometimes stored only the first token for multi-word champions
  // (for example "Master" for "Master Yi - ..."). Prefer the fuller title-derived identity.
  if (fromName && fromName !== raw && fromName.startsWith(`${raw} `)) return fromName;
  return raw;
}

/**
 * Chosen Champion candidate: Unit with Champion supertype (same rule as `/api/cards` normalization),
 * plus tag/name fallbacks for incomplete cached rows.
 */
export function isChampionUnitCard(card: ChampionCardRef): boolean {
  const st = (card.supertype || '').toLowerCase();
  const ct = (card.cardType || '').toLowerCase();
  if (ct !== 'unit') return false;
  return (
    st.includes('champion') ||
    Boolean(card.champion?.trim()) ||
    Boolean(championUnitIdentityFromTags(card.tags, card.name ?? ''))
  );
}

/** Champion identity slug for a champion unit, or '' when {@link isChampionUnitCard} is false. */
export function championUnitIdentity(card: ChampionCardRef): string {
  if (!isChampionUnitCard(card)) return '';
  const fromField = card.champion?.trim().toLowerCase();
  if (fromField) return fromField;
  const fromTags = championUnitIdentityFromTags(card.tags, card.name ?? '');
  if (fromTags) return fromTags;
  return championIdentityFromName(card.name ?? '');
}

/** Deck builder: card can be set as Chosen Champion for the selected legend. */
export function isEligibleChosenChampion(
  card: ChampionCardRef,
  legend: ChampionCardRef | null | undefined,
): boolean {
  if (!legend?.name?.trim()) return false;
  if (!isChampionUnitCard(card)) return false;
  return championIdentityMatchesLegend(
    { name: card.name ?? '', champion: card.champion, tags: card.tags },
    { name: legend.name, champion: legend.champion, tags: legend.tags },
  );
}

/** UI label for card type badges (Riftbound prints "Champion Unit" / "Signature Spell" on the card). */
export function cardTypeDisplayLabel(card: ChampionCardRef): string {
  if (isChampionUnitCard(card)) return 'Champion Unit';
  const line = cardTypeLineLabel(card) || (card.cardType ?? '').trim();
  // Signed legend prints stay "Legend". Playable Signature spells/gear print the prefix.
  if (isGenericSignatureSpell(card) && (card.cardType ?? '').toLowerCase() !== 'legend') {
    return line ? `Signature ${line}` : 'Signature';
  }
  return line;
}

/** Whether a card may be placed in the Chosen Champion slot for the given legend title. */
export function championIdentityMatchesLegend(
  card: { name: string; champion?: string; tags?: string[] },
  legend: string | { name: string; champion?: string; tags?: string[] }
): boolean {
  const a = identityFromRef(card);
  const b = typeof legend === 'string' ? identityFromRef({ name: legend }) : identityFromRef(legend);
  return a.length > 0 && b.length > 0 && a === b;
}

/**
 * Riftcodex `metadata.signature` / "(Signed)" name — signed/showcase print flag.
 * Older rows still say "(Signature)".
 *
 * For Legends this is still a Legend (slot-only showcase art), not a main-deck card.
 * Playable signature cards use {@link isGenericSignatureSpell} (`supertype: Signature`).
 */
export function isChampionSignatureCard(card: {
  cardType?: string;
  signature?: boolean;
  name?: string;
}): boolean {
  const type = (card.cardType ?? '').toLowerCase();
  // Playable Signature-supertype spells/units are not signed Legend prints,
  // even if a stale row still has `signature: true`.
  if (type && type !== 'legend') return false;
  if (card.signature) return true;
  // Fallback for cached rows synced before `signature` was normalized onto card_data.
  return type === 'legend' && /\(\s*(?:signature|signed)\s*\)/i.test(card.name ?? '');
}

/** Legend print with Signature showcase art — belongs only in the Legend slot. */
export function isSignatureLegendShowcase(card: {
  cardType?: string;
  signature?: boolean;
  name?: string;
}): boolean {
  return (card.cardType ?? '').toLowerCase() === 'legend' && isChampionSignatureCard(card);
}

/** Playable card with Signature supertype (e.g. Tibbers, Bullet Time) tied to a legend via tags / champion. */
export function isGenericSignatureSpell(card: { cardType?: string; supertype?: string }): boolean {
  return (card.supertype ?? '').toLowerCase() === 'signature';
}

/** Signature card that should only appear for the matching legend in deck browse. */
export function isSignatureRestrictedCard(card: {
  cardType?: string;
  supertype?: string;
  signature?: boolean;
}): boolean {
  return isChampionSignatureCard(card) || isGenericSignatureSpell(card);
}

/** Normalized champion identity for matching signature cards to the selected legend. */
export function signatureChampionIdentity(card: {
  name: string;
  champion?: string;
  tags?: string[];
}): string {
  const fromTags = tagChampionIdentity(card.tags, card.name);
  if (fromTags) return fromTags;
  const fromChampion = card.champion?.trim();
  if (fromChampion) return fromChampion.toLowerCase();
  if (isChampionSignatureCard(card)) {
    return championIdentityFromName(card.name);
  }
  return '';
}

export function signatureMatchesLegendChampion(
  card: { name: string; champion?: string; tags?: string[] },
  legendChampion: string
): boolean {
  const id = signatureChampionIdentity(card);
  return id.length > 0 && id === legendChampion;
}
